import MedicationCombobox from '@/components/common/medication-combobox'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import { format } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { DatePicker } from '../datepicker'
import TextareaWithLabel from '../textarea'

import { useSuspenseCurrentMedication } from '@/hooks/useCurrentMedication'
import { SetDateIcon, TooltipWrapper } from './previous-orders'
import { useToast } from '@/hooks/use-toast'
import { useMedicationOptions } from '@/hooks/useMedicationOptions'
import { usePreviousOrderDates } from '@/hooks/usePreviousOrderDates'
import { productLevelMedicationFor, toMedicationOption } from '@/lib/medication-options'
import { getCurrentUKDate, isFutureIssueDate, isTodayInUK } from '@/lib/utils'

export default function PrescriptionDetailsStandard({ isEditable }: { isEditable: boolean }) {
  const {
    medicationIssueDate,
    setMedicationIssueDate,
    medicationDirection,
    setMedicationDirection,
    medication,
    setMedication
  } = useMedicationRequestStore()

  const { toast } = useToast()
  const { data: medRequestData } = useSuspenseCurrentMedication()
  const { advancedPreviousOrderDate, lastIssuedDate } = usePreviousOrderDates()

  const isFutureDate = isFutureIssueDate(medicationIssueDate)

  const showNoMedicationDirectionBadge = isEditable && !medicationDirection

  const { options: parsedMedicationOptions, medications, findMedicationByHash } = useMedicationOptions()

  const onMedicationChange = (wooSKU: string) => {
    const medicationData = findMedicationByHash(wooSKU)
    if (!medicationData) throw new Error('Medication not found')

    if (medicationData.type === 'OTC') {
      setMedicationDirection('')
    } else {
      setMedicationDirection(medicationData.medicationDirections ?? '')
    }
    setMedication(medicationData)
  }

  const setMedicationFromMedReq = (wooSKU: string) => {
    const medicationData = findMedicationByHash(wooSKU)
    const medReqDirection = medRequestData.orderedItem.directions

    setMedicationDirection(medReqDirection || medicationData?.medicationDirections || '')
    setMedication(medicationData ?? null)
  }

  useEffect(() => {
    if (medications?.length) {
      const dataToUse = medRequestData?.orderedItem?.id
      setMedicationFromMedReq(dataToUse || '')
    }
  }, [medRequestData, medications])

  // A read-only request shows its product even when the item names no variation. The store stays
  // empty, so an editable request still waits for the prescriber to pick one.
  const orderedProduct = useMemo(
    () =>
      isEditable || medication
        ? undefined
        : productLevelMedicationFor(medications ?? [], medRequestData?.orderedItem?.id ?? ''),
    [isEditable, medication, medications, medRequestData?.orderedItem?.id]
  )

  // The product has no row in the catalogue, so it is added for the trigger to show its label.
  const medicationOptions = useMemo(
    () =>
      orderedProduct
        ? [toMedicationOption(orderedProduct), ...(parsedMedicationOptions ?? [])]
        : (parsedMedicationOptions ?? []),
    [orderedProduct, parsedMedicationOptions]
  )

  return (
    <div data-testid='prescription-details'>
      <p
        id='prescription-details'
        className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] font-feature-settings-liga-off font-feature-settings-clig-off mb-6'
      >
        Prescription details
      </p>
      <p id='select-medication' className='text-[#1D1D1D]  text-base font-medium leading-[170%] mb-4'>
        Select medication
      </p>
      <MedicationCombobox
        isDisabled={!isEditable}
        options={medicationOptions}
        onValueChange={onMedicationChange}
        title='Medication'
        defaultValue={medication?.wooSKU ?? orderedProduct?.wooSKU}
      />

      <p id='medication-direction' className=' text-[#1D1D1D] text-base font-medium leading-[170%] my-4'>
        Medication direction
      </p>
      {showNoMedicationDirectionBadge && medication?.type !== 'OTC' && (
        <div className='mb-4 rounded-2xl mt-4 border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'>
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            This medication doesn't have any directions. Please
            <b>{''} add directions before approving</b> the medication request.
          </p>
        </div>
      )}

      <TextareaWithLabel
        isDisabled={!isEditable}
        value={medicationDirection}
        title='Direction'
        onChange={e => setMedicationDirection(e.target.value)}
        hasError={showNoMedicationDirectionBadge && medication?.type !== 'OTC'}
      />

      <div className='flex flex-col gap-[2.18rem]'>
        <p id='issue-date' className=' text-[#1D1D1D] text-base font-medium leading-[170%] my-4'>
          Issue Date
        </p>
        <div
          data-testid='medication-issue-date'
          className='rounded-2xl border border-custom-neutral-200 bg-[#EBEFFA] flex px-6 py-4 justify-between items-center'
        >
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>Medication Issue Date</p>

          <div className='flex flex-col gap-3 w-max'>
            <div className='flex gap-[26px]'>
              <p className='text-[#080F30] text-base font-semibold leading-[170%]'>
                {format(medicationIssueDate, 'd MMMM yyyy')} {isTodayInUK(medicationIssueDate) ? '(Today)' : ''}
              </p>

              <DatePicker
                isDisabled={!isEditable}
                fromDate={getCurrentUKDate()}
                date={medicationIssueDate}
                setDate={picked => {
                  const theDate = new Date(picked ?? new Date())
                  // default at noon to prevent timezone date flips
                  theDate.setHours(12, 0, 0, 0)
                  setMedicationIssueDate(theDate)
                }}
              />
            </div>
          </div>
        </div>

        {lastIssuedDate && (
          <div className='flex items-center gap-4'>
            <span className='font-poppins text-base leading-7 font-bold text-[#1D1D1D]'>Last issued date:</span>
            <span className='font-poppins text-base leading-7 text-[#4E545E]'>
              {format(lastIssuedDate, 'd MMMM yyyy')}
            </span>
            <TooltipWrapper
              trigger={
                <button
                  disabled={!isEditable}
                  type='button'
                  className='flex items-center gap-1 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
                  onClick={() => {
                    setMedicationIssueDate(advancedPreviousOrderDate)
                    toast({
                      title: 'Issue date set',
                      description: `Issue date set to [${format(advancedPreviousOrderDate, 'dd MMMM yyyy')}]`
                    })
                  }}
                >
                  <SetDateIcon />
                </button>
              }
              content={
                <p className='text-white text-xs font-medium leading-4'>Set issue date to 12 weeks from this date</p>
              }
            />
          </div>
        )}
      </div>

      {isFutureDate && (
        <div className='rounded-2xl mt-4 border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'>
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            This request is scheduled for approval on <b>{format(medicationIssueDate, 'MMMM d, yyyy')}.</b>
          </p>
        </div>
      )}
    </div>
  )
}
